/**
 * 令和8年法改正 — 施術 on or after this date carries the 明細書発行加算. Compared as a plain
 * YYYY-MM-DD string against the visit date.
 *
 * Lived in VisitScheduleModal.constants while 訪問入力 also showed the checkbox; it moved here
 * with the helper when that screen dropped it.
 */
export const LAW_REVISION_DATE_2026 = '2026-07-01';

import type { MeisaiIssueVisitStatus } from '@/services/main/meisaiIssueSetting';

/**
 * 令和8年法改正 No 5 — is the visit being edited the earliest of the patient's 施術月?
 *
 * Under 月別 only that visit may carry the 明細書発行加算, so it is the only one that shows
 * the checkbox. The server can only answer for SAVED data, while the [来院]施術入力 calendar
 * stages a whole month of adds and deletes before any of it is written — add a visit on the
 * 5th while one already exists on the 10th and the 10th must stop offering the box straight
 * away, not after 登録.
 *
 * 訪問入力 used to show this checkbox too and shared this helper; that screen no longer does,
 * so the file now lives next to its only caller.
 *
 * Only 来院 rows are in the running — `month_visits` already excludes what 訪問入力 created,
 * and the caller filters its staged rows the same way, so a 訪問 on the 1st does not stop a
 * 来院 on the 2nd from being the month's earliest.
 *
 * So the saved month (`month_visits`, across every 施術師) is overlaid with what is staged:
 *   ・rows queued for deletion drop out
 *   ・rows moved locally use their staged date
 *   ・drafts that do not exist server-side are added
 *
 * Ties go to the row that is already saved, then to the lower id — the same order
 * MeisaiIssueFlagResolver uses, so the form and the server agree on which visit wins.
 */
export interface StagedVisit {
  id: number | string;
  /** YYYY-MM-DD */
  visitDate: string;
}

interface Candidate {
  key: string;
  date: string;
  saved: boolean;
  id: number;
}

const toCandidate = (id: number | string, date: string, saved: boolean): Candidate => ({
  key: String(id),
  date,
  saved,
  id: typeof id === 'number' ? id : Number.MAX_SAFE_INTEGER,
});

/** Earlier date wins; then a saved row beats a draft; then the lower id. */
const isBefore = (a: Candidate, b: Candidate): boolean => {
  if (a.date !== b.date) return a.date < b.date;
  if (a.saved !== b.saved) return a.saved;
  return a.id < b.id;
};

export function resolveIsEarliestOfMonth(
  status: MeisaiIssueVisitStatus | undefined,
  staged: StagedVisit[],
  deletedIds: number[],
  edited: StagedVisit
): boolean {
  // No month data yet — fall back to the server's answer for saved rows, and to "yes" while
  // nothing is known, so the box is never hidden on a guess.
  if (!status) return true;

  const dropped = new Set(deletedIds.map(String));
  const stagedByKey = new Map(staged.map(v => [String(v.id), v]));

  const candidates: Candidate[] = [];

  for (const saved of status.month_visits ?? []) {
    const key = String(saved.id);
    if (dropped.has(key)) continue;
    // A locally moved row is judged on where it now sits, not where it was saved.
    const local = stagedByKey.get(key);
    candidates.push(toCandidate(saved.id, local?.visitDate ?? saved.visit_date, true));
  }

  const savedKeys = new Set((status.month_visits ?? []).map(v => String(v.id)));
  for (const local of staged) {
    const key = String(local.id);
    if (savedKeys.has(key) || dropped.has(key)) continue;
    candidates.push(toCandidate(local.id, local.visitDate, false));
  }

  const editedKey = String(edited.id);
  const editedCandidate = toCandidate(
    edited.id,
    edited.visitDate,
    savedKeys.has(editedKey) && !stagedByKey.has(editedKey)
  );
  // The row being edited may not be in either list yet (a visit still being created), and its
  // date can differ from both — always judge it on what the form currently shows.
  const withoutEdited = candidates.filter(c => c.key !== editedKey);

  return !withoutEdited.some(other => isBefore(other, editedCandidate));
}
